import React, { useMemo } from "react"
import { DbgPaths } from "#/utils/Paths"
import { getMedicationCategorySections } from "./multiple-categories/MedicationsCategory"
import { getQmeCategorySections } from "./multiple-categories/QmeCategory"
import useBenefit from "../../../dynamic-content/hooks/useGetBenefit"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { FinancialSupportErrorMessage } from "./FinancialSupportErrorMessage"
import useCompanyComplianceConfigLineItemFilter from "../hooks/useGetCompanyComplianceConfigLineItemFilter"
import {
  Box,
  CloseIcon,
  Dialog,
  DialogTitle,
  IconButton,
  Link,
  Progress,
  Stack,
  Typography,
  UnorderedList,
  UnorderedListItem
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { useSelector } from "react-redux"
import { useHistory } from "react-router"
import EmployeeOnlyModalContainer from "../../../employee-only-containers/EmployeeOnlyModalContainer"
import { getCategoryName } from "../ReimbursementCategoryMessages"
import { CategoryCoverage, getCategoryCoverage } from "#/utils/CategoryCoverage"
import { getSubsidies } from "#/redux/reducers/subsidies"
import { FormattedSubsidy } from "#/components/views/reimbursements/digital-benefit-guide/PayingForCare"
import { getPreservationSections } from "./multiple-categories/PreservationCategory"
import { getFertilityAndPreservationSections } from "./multiple-categories/FertilityAndPreservationCategory"
import { getFertilitySections } from "./multiple-categories/FertilityCategory"
import {
  getOtherExpensesSections,
  OtherExpensesCardCopy,
  otherCardHasList
} from "./multiple-categories/OtherExpensesCategory"
import { getMenopauseLowTestosteroneSections } from "./multiple-categories/MenopauseLowTestosteroneCategory"
import { getAdoptionAndGCSections } from "./multiple-categories/AdoptionAndGCCategory"
import { reimbursementCategoryNameMessageKeys } from "../reimbursementCategoryNameMessageKeys"
import { TileAccordion } from "#/components/views/molecules/TileAccordion"

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
const PayingForCareContentSub = (props) => {
  const { category, benefitInfo, lineItemFilter, hasQmeCategory, hasMedicationsCategory } = props
  const isOtherExpenses = useMemo(() => category === reimbursementCategoryNameMessageKeys.OTHER, [category])
  const preservation = benefitInfo.journeys.fertilityPreservation
  const fertilityCare = benefitInfo.journeys.fertilityCare
  const hasElectiveFertilityLineItemFilter = lineItemFilter === "ELECTIVE_FERTILITY"
  const showQmeInfertilityMessage =
    fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis ||
    preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered
  const showOtherExpensesInfertilityMessage =
    fertilityCare.eligibleExpensesDetail.isElectiveCareCovered ||
    preservation.eligibleExpensesDetail.isElectivePreservationCovered

  return (
    <>
      {!hasElectiveFertilityLineItemFilter &&
        hasQmeCategory &&
        !hasMedicationsCategory &&
        (showQmeInfertilityMessage || showOtherExpensesInfertilityMessage) && (
          <Box>
            <Typography variant="h6">
              <FormattedMessage defaultMessage="How do I know if I have an infertility diagnosis?" />
            </Typography>
            <Box
              sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs, display: "flex", flexDirection: "column" })}
            >
              <Typography>
                <FormattedMessage
                  defaultMessage={
                    "If you’re not sure, ask your fertility doctor. Based on local standards" +
                    " of practice, a doctor may diagnose infertility if pregnancy does not occur within a" +
                    " specific timeframe of unprotected intercourse or insemination."
                  }
                />
              </Typography>
            </Box>
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
              <Typography>
                <FormattedMessage defaultMessage="An infertility diagnosis may also be issued due to:" />
              </Typography>
            </Box>
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
              <UnorderedList>
                <UnorderedListItem>
                  <Typography>
                    <FormattedMessage defaultMessage="Ovulation issues or diminished ovarian reserve" />
                  </Typography>
                </UnorderedListItem>
                <UnorderedListItem>
                  <Typography>
                    <FormattedMessage defaultMessage="Problems with semen, sperm, or ejaculation" />
                  </Typography>
                </UnorderedListItem>
                <UnorderedListItem>
                  <Typography>
                    <FormattedMessage defaultMessage="Anatomical, endocrine, genetic, or functional differences in the reproductive system" />
                  </Typography>
                </UnorderedListItem>
              </UnorderedList>
            </Box>
          </Box>
        )}
      <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
        {!isOtherExpenses &&
          !hasElectiveFertilityLineItemFilter &&
          preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered && (
            <Box>
              <Typography variant="h6">
                <FormattedMessage defaultMessage="How do I know if my preservation care is medically necessary?" />
              </Typography>
              <Box
                sx={(theme) => ({
                  marginBlockStart: theme.tokens.spacing.xs,
                  display: "flex",
                  flexDirection: "column"
                })}
              >
                <Typography>
                  <FormattedMessage
                    defaultMessage={
                      "If you’re not sure, ask your fertility doctor. Medical necessity" +
                      " for fertility preservation is determined by a doctor based on health issues or treatments that" +
                      " could significantly impact the reproductive system. For example, preservation may be considered " +
                      "medically necessary before undergoing certain types of chemotherapy or surgery."
                    }
                  />
                </Typography>
              </Box>
            </Box>
          )}
        {isOtherExpenses &&
          !hasElectiveFertilityLineItemFilter &&
          hasQmeCategory &&
          !hasMedicationsCategory &&
          preservation.eligibleExpensesDetail.isElectivePreservationCovered && (
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
              <Typography variant="h6">
                <FormattedMessage defaultMessage="How do I know if my care is medically necessary?" />
              </Typography>
              <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
                <Typography>
                  <FormattedMessage
                    defaultMessage={
                      "If you’re not sure, ask your fertility doctor. Medical necessity" +
                      " for fertility preservation is determined by a doctor based on health issues or treatments that" +
                      " could significantly impact the reproductive system. For example, preservation may be considered " +
                      "medically necessary before undergoing certain types of chemotherapy or surgery."
                    }
                  />
                </Typography>
              </Box>
            </Box>
          )}
      </Box>
    </>
  )
}

function coverageLink(href: string) {
  return function CoverageLink(linkContent: React.ReactNode) {
    return (
      <Link target="_blank" href={href}>
        {linkContent}
      </Link>
    )
  }
}

export const SingleJourneyDescription = ({ category }: { category: string }): JSX.Element | null => {
  switch (category) {
    case reimbursementCategoryNameMessageKeys.FERTILITY:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>fertility expenses</link>."
          values={{ link: coverageLink(DbgPaths.FERTILITY_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.PRESERVATION:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>fertility preservation expenses</link>."
          values={{ link: coverageLink(DbgPaths.PRESERVATION_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.ADOPTION:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>adoption expenses</link>."
          values={{ link: coverageLink(DbgPaths.ADOPTION_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.GC:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>gestational surrogacy expenses</link>."
          values={{ link: coverageLink(DbgPaths.GESTATIONAL_SURROGACY_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.PREGNANCY:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>pregnancy and postpartum expenses</link>."
          values={{ link: coverageLink(DbgPaths.PREGNANCY_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.GAC:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>gender-affirming care expenses</link>."
          values={{ link: coverageLink(DbgPaths.GENDER_AFFIRMING_CARE) }}
        />
      )
    default:
      return null
  }
}

// Card-only copy; null falls back to the shared sections body.
function getCoveredExpensesCardCopy(category: string, coverage: CategoryCoverage): React.ReactNode[] | null {
  switch (category) {
    case reimbursementCategoryNameMessageKeys.QME:
      return [
        <FormattedMessage
          key="qme"
          defaultMessage="This category includes eligible <link>qualified medical expenses</link>."
          values={{ link: coverageLink(DbgPaths.QUALIFIED_MEDICAL_EXPENSES) }}
        />
      ]
    case reimbursementCategoryNameMessageKeys.ADOPTION:
      return [
        <FormattedMessage
          key="adoption"
          defaultMessage="This category includes eligible <link>adoption</link> expenses."
          values={{ link: coverageLink(DbgPaths.ADOPTION_COVERAGE) }}
        />
      ]
    case reimbursementCategoryNameMessageKeys.FERTILITY:
      return [
        <FormattedMessage
          key="fertility"
          defaultMessage="This category includes eligible <link>fertility care</link> expenses."
          values={{ link: coverageLink(DbgPaths.FERTILITY_COVERAGE) }}
        />,
        coverage.preservation && (
          <FormattedMessage
            key="fertility-definition"
            defaultMessage="Fertility care involves actively trying for a pregnancy, including up to one year of related storage costs for eggs, sperm, and embryos."
          />
        )
      ]
    case reimbursementCategoryNameMessageKeys.PRESERVATION:
      return [
        <FormattedMessage
          key="preservation"
          defaultMessage="This category includes eligible <link>fertility preservation</link> expenses."
          values={{ link: coverageLink(DbgPaths.PRESERVATION_COVERAGE) }}
        />,
        coverage.fertility && (
          <FormattedMessage
            key="preservation-definition"
            defaultMessage="Fertility preservation involves freezing eggs, sperm, or embryos for future use. This means you do not intend to use the material (thaw, inseminate, transfer, etc.) for at least one year after freezing it."
          />
        )
      ]
    case reimbursementCategoryNameMessageKeys.GAC:
      return [
        <FormattedMessage
          key="gac"
          defaultMessage="This category includes eligible <link>gender-affirming care</link> expenses."
          values={{ link: coverageLink(DbgPaths.GENDER_AFFIRMING_CARE) }}
        />
      ]
    case reimbursementCategoryNameMessageKeys.GC:
      return [
        <FormattedMessage
          key="gc"
          defaultMessage="This category includes eligible <link>gestational surrogacy</link> expenses."
          values={{ link: coverageLink(DbgPaths.GESTATIONAL_SURROGACY_COVERAGE) }}
        />
      ]
    case reimbursementCategoryNameMessageKeys.PREGNANCY:
      return [
        <FormattedMessage
          key="pregnancy"
          defaultMessage="This category includes eligible <link>pregnancy and postpartum</link> expenses."
          values={{ link: coverageLink(DbgPaths.PREGNANCY_COVERAGE) }}
        />
      ]
    default:
      return null
  }
}

// Body shared by the Learn modal and the Covered expenses card.
const CategorySectionsBody = ({
  category,
  benefit,
  lineItemFilter,
  hasQmeCategory,
  hasMedicationsCategory
}: {
  category: string
  benefit: Benefit
  lineItemFilter: unknown
  hasQmeCategory: boolean
  hasMedicationsCategory: boolean
}): JSX.Element => {
  const sections = getCategorySections(category, hasQmeCategory)
  return (
    <Box>
      <Typography>{sections.header}</Typography>
      {(sections.listItems || sections.footer) && (
        <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
          {sections.listItems && <UnorderedList>{sections.listItems}</UnorderedList>}
          {sections.footer && (
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
              <Typography>{sections.footer}</Typography>
            </Box>
          )}
        </Box>
      )}
      {sections.includeSub && (
        <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
          <PayingForCareContentSub
            category={category}
            benefitInfo={benefit}
            lineItemFilter={lineItemFilter}
            hasQmeCategory={hasQmeCategory}
            hasMedicationsCategory={hasMedicationsCategory}
          />
        </Box>
      )}
    </Box>
  )
}

const CoveredExpensesSections = ({
  category,
  coverage,
  benefit
}: {
  category: string
  coverage: CategoryCoverage
  benefit: Benefit
}): JSX.Element => {
  const { data, isLoading } = useCompanyComplianceConfigLineItemFilter()
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Progress />
      </Box>
    )
  }
  if (data == null) return <FinancialSupportErrorMessage />

  return (
    <CategorySectionsBody
      category={category}
      benefit={benefit}
      lineItemFilter={data}
      hasQmeCategory={coverage.qme}
      hasMedicationsCategory={coverage.medication}
    />
  )
}

const CoveredExpensesContent = ({
  category,
  coverage
}: {
  category: string
  coverage: CategoryCoverage
}): JSX.Element => {
  const { data: benefit, isLoading } = useBenefit()

  const cardCopy = getCoveredExpensesCardCopy(category, coverage)?.filter(Boolean)
  if (cardCopy) {
    return (
      <Stack sx={(theme) => ({ gap: theme.tokens.spacing.xs })}>
        {cardCopy.map((paragraph, i) => (
          <Typography key={i}>{paragraph}</Typography>
        ))}
      </Stack>
    )
  }
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Progress />
      </Box>
    )
  }
  if (benefit == null) {
    return <FinancialSupportErrorMessage />
  }
  if (category === reimbursementCategoryNameMessageKeys.OTHER) {
    return <OtherExpensesCardCopy coverage={coverage} benefit={benefit} />
  }

  return <CoveredExpensesSections category={category} coverage={coverage} benefit={benefit} />
}

export function CoveredExpensesCard({
  category,
  coverage
}: {
  category: string
  coverage: CategoryCoverage
}): JSX.Element {
  const { data: benefit } = useBenefit()
  // One sentence: no caret.
  const cardCopy = getCoveredExpensesCardCopy(category, coverage)?.filter(Boolean)
  const collapsible = cardCopy
    ? cardCopy.length > 1
    : category !== reimbursementCategoryNameMessageKeys.OTHER || benefit == null || otherCardHasList(coverage, benefit)
  return (
    <TileAccordion
      id={`covered-expenses-${category}`}
      color="blue"
      title={<FormattedMessage defaultMessage="Covered expenses" />}
      collapsible={collapsible}
    >
      <CoveredExpensesContent category={category} coverage={coverage} />
    </TileAccordion>
  )
}

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
const PayingForCareContentMain = (props): JSX.Element => {
  const { category, hasQmeCategory, hasMedicationsCategory } = props
  const { data: benefit, isLoading } = useBenefit()
  const { data } = useCompanyComplianceConfigLineItemFilter()
  const coverage = getCategoryCoverage(useSelector(getSubsidies))
  const intl = useIntl()

  if ((data == null || benefit == null) && !isLoading) {
    return <FinancialSupportErrorMessage />
  }

  return isLoading ? (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Progress />
    </Box>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={(theme) => ({ padding: "none", marginBlockEnd: theme.tokens.spacing.xl })}>
        <DialogTitle id="fund-category-name">{intl.formatMessage(getCategoryName(category, coverage.qme))}</DialogTitle>
        <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
          <CategorySectionsBody
            category={category}
            benefit={benefit}
            lineItemFilter={data}
            hasQmeCategory={hasQmeCategory}
            hasMedicationsCategory={hasMedicationsCategory}
          />
        </Box>
      </Box>
    </Box>
  )
}

export function PayingForCareCoverageModal({
  subsidyCategory,
  hasQmeCategory,
  hasMedicationsCategory
}: {
  subsidyCategory: FormattedSubsidy
  hasQmeCategory: boolean
  hasMedicationsCategory: boolean
}): JSX.Element {
  const [showModal, setShowModal] = React.useState(false)
  const intl = useIntl()

  return (
    <>
      <CategoryLearnLink
        category={subsidyCategory.reimbursementCategoryNameMessageKey}
        handleOpen={() => setShowModal(true)}
      />
      <EmployeeOnlyModalContainer onClose={() => setShowModal(false)}>
        <Dialog
          aria-labelledby="fund-category-name"
          onClose={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            setShowModal(false)
          }}
          open={showModal}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end"
            }}
          >
            <IconButton
              aria-label={intl.formatMessage({ defaultMessage: "close" })}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                setShowModal(false)
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <PayingForCareContentMain
            category={subsidyCategory.reimbursementCategoryNameMessageKey}
            hasQmeCategory={hasQmeCategory}
            hasMedicationsCategory={hasMedicationsCategory}
          />
          <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xxxl })}></Box>
        </Dialog>
      </EmployeeOnlyModalContainer>
    </>
  )
}

function CategoryLearnLink({ category, handleOpen }: { category: string; handleOpen: () => void }): React.JSX.Element {
  const { data: benefit } = useBenefit()
  const history = useHistory()

  let href: string = null
  switch (category) {
    case reimbursementCategoryNameMessageKeys.ADOPTION: {
      href = DbgPaths.ADOPTION_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.GC: {
      href = DbgPaths.GESTATIONAL_SURROGACY_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY: {
      if (
        !benefit?.journeys.fertilityCare.eligibleExpensesDetail.isElectiveCareCovered &&
        !benefit?.journeys.fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis
      ) {
        href = DbgPaths.FERTILITY_COVERAGE
      }
      break
    }
    case reimbursementCategoryNameMessageKeys.GAC: {
      href = DbgPaths.GENDER_AFFIRMING_CARE
      break
    }
    case reimbursementCategoryNameMessageKeys.PREGNANCY: {
      href = DbgPaths.PREGNANCY_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.PRESERVATION: {
      if (
        benefit?.journeys.fertilityPreservation.eligibleExpensesDetail.isElectivePreservationCovered ||
        benefit?.journeys.fertilityPreservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered ||
        benefit?.journeys.fertilityCare.eligibleExpensesDetail.hasDonorAssistance
      ) {
        href = DbgPaths.PRESERVATION_COVERAGE
      }
      break
    }
  }

  return (
    <Typography
      variant="body1"
      color={(theme) => theme.palette.text.secondary}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: (theme) => theme.spacing(theme.tokens.spacing.xxs)
      }}
    >
      <FormattedMessage
        defaultMessage="<link>Learn</link> what this includes"
        values={{
          link: (linkContent) => (
            <Link
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                if (href) {
                  history.push(href)
                }
                handleOpen()
              }}
              color="inherit"
              component="button"
              sx={{
                pointerEvents: "auto"
              }}
            >
              {linkContent}
            </Link>
          )
        }}
      />
    </Typography>
  )
}

export interface CategorySections {
  header: React.JSX.Element
  listItems?: React.JSX.Element
  footer?: React.JSX.Element
  includeSub?: boolean
}

export function getCategorySections(category: string, hasQmeCategory: boolean): CategorySections {
  switch (category) {
    case reimbursementCategoryNameMessageKeys.FERTILITY_GC_ADOPTION:
    case reimbursementCategoryNameMessageKeys.ADOPTION_AND_GC: {
      return getAdoptionAndGCSections(category)
    }
    case reimbursementCategoryNameMessageKeys.MEDICATION: {
      return getMedicationCategorySections()
    }
    case reimbursementCategoryNameMessageKeys.QME: {
      return getQmeCategorySections()
    }
    case reimbursementCategoryNameMessageKeys.M_LT:
    case reimbursementCategoryNameMessageKeys.M_LT_GAC:
    case reimbursementCategoryNameMessageKeys.PREGNANCY_M_LT:
    case reimbursementCategoryNameMessageKeys.FERTILITY_M_LT: {
      return getMenopauseLowTestosteroneSections(category)
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY: {
      return getFertilitySections()
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY_GC_ADOPTION:
    case reimbursementCategoryNameMessageKeys.FERTILITY_AND_PRESERVATION: {
      return getFertilityAndPreservationSections()
    }
    case reimbursementCategoryNameMessageKeys.PRESERVATION: {
      return getPreservationSections()
    }
  }
  return getOtherExpensesSections(hasQmeCategory)
}
